<template>
  <div class="punch-page">
    <div class="punch-header">
      <div class="punch-header-left">
        <div class="punch-header-icon">
          <i class="bi bi-geo-alt-fill"></i>
        </div>
        <div>
          <h5 class="punch-title mb-0">Time In / Out</h5>
          <small class="punch-sub">
            {{ windowSummary }} · {{ fencesSummary }} · live photo required
          </small>
        </div>
      </div>
      <span class="next-badge" :class="nextStatus === 'IN' ? 'next-in' : 'next-out'">
        Next: {{ punchLabel }}
      </span>
    </div>

    <div v-if="passSlipNotice && canPunch" class="punch-alert punch-info">
      <i class="bi bi-info-circle-fill mr-2"></i>
      {{ passSlipNotice }}
    </div>

    <div v-if="statusMessage && !canPunch" class="punch-alert">
      <i class="bi bi-exclamation-triangle-fill mr-2"></i>
      {{ statusMessage }}
    </div>

    <template v-if="canPunch">
      <ol class="wizard-steps">
        <li
          v-for="s in wizardSteps"
          :key="s.id"
          class="wizard-step"
          :class="{
            'is-active': step === s.id,
            'is-done': step > s.id,
          }"
        >
          <span class="wizard-num">
            <i v-if="step > s.id" class="bi bi-check-lg"></i>
            <template v-else>{{ s.id }}</template>
          </span>
          <span class="wizard-label">{{ s.label }}</span>
        </li>
      </ol>

      <section class="punch-panel wizard-panel">
        <!-- Step 1: Location -->
        <div v-show="step === 1" class="wizard-body">
          <div class="panel-head">
            <div>
              <h6>Confirm your location</h6>
              <p class="step-lead">Stay at the office until both checks are green, then continue.</p>
            </div>
            <button
              type="button"
              class="btn-refresh"
              :class="{ spinning: locating }"
              :disabled="locating"
              title="Refresh location"
              @click="refreshLocation"
            >
              <i class="bi bi-arrow-clockwise"></i>
            </button>
          </div>

          <div class="check-list">
            <div class="check-item" :class="locationCheckClass">
              <div class="check-icon"><i class="bi" :class="locationIcon"></i></div>
              <div>
                <div class="check-label">Location</div>
                <div class="check-text">{{ locationHint }}</div>
              </div>
            </div>
            <div class="check-item" :class="accuracyCheckClass">
              <div class="check-icon"><i class="bi" :class="accuracyIcon"></i></div>
              <div>
                <div class="check-label">GPS accuracy</div>
                <div class="check-text">{{ accuracyHint }}</div>
              </div>
            </div>
          </div>

          <div v-if="locationTip" class="step-tip">
            <i class="bi bi-info-circle-fill"></i>
            {{ locationTip }}
          </div>

          <div class="punch-map-wrap">
            <div ref="punchMap" class="punch-map"></div>
            <div v-if="!coords" class="punch-map-empty">
              <i class="bi bi-map"></i>
              <span>Waiting for GPS…</span>
            </div>
            <div class="punch-map-legend">
              <span class="leg-office">Office</span>
              <span class="leg-you" :class="insideFence ? 'ok' : 'bad'">You</span>
            </div>
          </div>

          <p v-if="coords" class="coord-note">
            {{ coords.lat.toFixed(5) }}, {{ coords.lng.toFixed(5) }}
          </p>

          <div class="wizard-actions">
            <button
              type="button"
              class="btn-punch-outline"
              :disabled="locating"
              @click="refreshLocation"
            >
              Refresh location
            </button>
            <button
              type="button"
              class="btn-punch-primary"
              :disabled="!locationReady"
              @click="goToPhoto"
            >
              Continue
            </button>
          </div>
        </div>

        <!-- Step 2: Photo -->
        <div v-show="step === 2" class="wizard-body">
          <div class="panel-head">
            <div>
              <h6>Take a live photo</h6>
              <p class="step-lead">Use the camera only — file upload is not allowed.</p>
            </div>
          </div>

          <div class="camera-wrap" :class="{ ready: cameraReady && !photoPreview, captured: !!photoPreview }">
            <video v-show="!photoPreview" ref="video" class="camera-view" playsinline autoplay muted></video>
            <img v-if="photoPreview" :src="photoPreview" class="camera-view" alt="Captured punch photo" />
            <canvas ref="canvas" class="d-none"></canvas>
            <div v-if="!photoPreview && !cameraReady" class="camera-empty">
              <i class="bi bi-camera-video"></i>
              <span>{{ cameraBusy ? "Starting camera…" : "Enable camera to continue" }}</span>
            </div>
          </div>

          <div class="camera-actions">
            <button
              type="button"
              class="btn-punch-outline"
              :disabled="cameraBusy"
              @click="photoPreview ? retakePhoto() : startCamera()"
            >
              {{ photoPreview ? "Retake" : "Start camera" }}
            </button>
            <button
              type="button"
              class="btn-punch-primary"
              :disabled="!cameraReady || cameraBusy || !!photoPreview"
              @click="capturePhoto"
            >
              Capture
            </button>
          </div>

          <div class="wizard-actions">
            <button type="button" class="btn-punch-outline" @click="goToLocation">
              Back
            </button>
            <button
              type="button"
              class="btn-punch-primary"
              :disabled="!photoBlob"
              @click="goToConfirm"
            >
              Continue
            </button>
          </div>
        </div>

        <!-- Step 3: Confirm -->
        <div v-show="step === 3" class="wizard-body">
          <div class="panel-head">
            <div>
              <h6>Confirm {{ punchLabel.toLowerCase() }}</h6>
              <p class="step-lead">Review the details below, then submit.</p>
            </div>
          </div>

          <div class="confirm-grid">
            <div class="confirm-main">
              <div class="confirm-row">
                <span class="confirm-label">Action</span>
                <span class="confirm-value">
                  <span class="status-badge" :class="nextStatus === 'IN' ? 'badge-in' : 'badge-out'">
                    {{ punchLabel }}
                  </span>
                </span>
              </div>
              <div class="confirm-row">
                <span class="confirm-label">Location</span>
                <span class="confirm-value">{{ locationHint }}</span>
              </div>
              <div class="confirm-row">
                <span class="confirm-label">GPS accuracy</span>
                <span class="confirm-value">{{ accuracyHint }}</span>
              </div>
              <div v-if="coords" class="confirm-row">
                <span class="confirm-label">Coordinates</span>
                <span class="confirm-value mono">
                  {{ coords.lat.toFixed(5) }}, {{ coords.lng.toFixed(5) }}
                </span>
              </div>
            </div>
            <div class="confirm-photo">
              <img v-if="photoPreview" :src="photoPreview" alt="Punch photo preview" />
              <div v-else class="confirm-photo-empty">No photo</div>
            </div>
          </div>

          <div class="wizard-actions">
            <button type="button" class="btn-punch-outline" :disabled="submitting" @click="goToPhoto">
              Back
            </button>
            <button
              type="button"
              class="btn-punch-submit"
              :class="nextStatus === 'IN' ? 'is-in' : 'is-out'"
              :disabled="!canSubmit || submitting"
              @click="submitPunch"
            >
              <span v-if="submitting" class="spinner-border spinner-border-sm mr-2"></span>
              <i v-else class="bi bi-check2-circle mr-1"></i>
              {{ punchLabel }}
            </button>
          </div>
        </div>
      </section>
    </template>

    <section v-if="personnelLinked" class="punch-panel today-panel">
        <div class="panel-head">
          <h6>Today’s punches</h6>
          <span class="panel-hint">{{ logs.length }} recorded</span>
        </div>

        <div v-if="!logs.length" class="today-empty">No punches yet today.</div>
        <ul v-else class="today-list">
          <li v-for="(log, idx) in logs" :key="log.id || idx">
            <div class="log-main">
              <span class="log-time">{{ formatTime(log.time) }}</span>
              <span class="status-badge" :class="log.inOutStatus === 'IN' ? 'badge-in' : 'badge-out'">
                {{ log.inOutStatus }}
              </span>
              <span class="source-badge" :class="log.source === 'web' ? 'src-web' : 'src-bio'">
                {{ log.source === 'web' ? 'System' : 'Biometric' }}
              </span>
            </div>
          </li>
        </ul>
      </section>
  </div>
</template>

<script>
import axios from "axios";
import toastr from "toastr";

function haversineMeters(lat1, lng1, lat2, lng2) {
  const earth = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return earth * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const LEAFLET_CSS = "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS = "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.js";

function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = LEAFLET_CSS;
    document.head.appendChild(link);
  }
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.L));
      existing.addEventListener("error", reject);
      if (window.L) resolve(window.L);
      return;
    }
    const script = document.createElement("script");
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default {
  name: "AttendancePunch",
  data() {
    return {
      step: 1,
      wizardSteps: [
        { id: 1, label: "Location" },
        { id: 2, label: "Photo" },
        { id: 3, label: "Confirm" },
      ],
      canPunch: false,
      personnelLinked: false,
      punchWindow: null,
      passSlipReturn: null,
      statusMessage: null,
      fence: {
        label: "CHED Caraga Regional Office",
        lat: 8.959581926078696,
        lng: 125.59747163125807,
        radius_meters: 50,
      },
      fences: [
        {
          label: "CHED Caraga Regional Office",
          lat: 8.959581926078696,
          lng: 125.59747163125807,
          radius_meters: 50,
        },
        {
          label: "CHED Caraga Office (Site 2)",
          lat: 8.956917698750319,
          lng: 125.57855713837651,
          radius_meters: 50,
        },
      ],
      maxAccuracy: 200,
      nextStatus: "IN",
      punchLabel: "Time In",
      logs: [],
      locating: false,
      gpsError: null,
      coords: null,
      distanceMeters: null,
      cameraReady: false,
      cameraBusy: false,
      photoPreview: null,
      photoBlob: null,
      mediaStream: null,
      submitting: false,
      punchInFlight: false,
      map: null,
      mapLayers: {
        fenceCircles: [],
        officeMarkers: [],
        youMarker: null,
        accuracyCircle: null,
      },
    };
  },
  computed: {
    fencesSummary() {
      const count = (this.fences && this.fences.length) || 1;
      const radius = (this.fence && this.fence.radius_meters) || 50;
      if (count > 1) return `${count} geo-fences · ${radius} m each`;
      return `${(this.fence && this.fence.label) || "Office"} · ${radius} m fence`;
    },
    windowSummary() {
      const w = this.punchWindow;
      if (!w || !w.enabled) return "Open all day";
      return `Open ${w.label || `${w.start}–${w.end}`}`;
    },
    passSlipNotice() {
      return this.passSlipReturn && this.passSlipReturn.message
        ? this.passSlipReturn.message
        : "";
    },
    insideFence() {
      if (this.distanceMeters == null || !this.fence) return false;
      return this.distanceMeters <= (this.fence.radius_meters || 50);
    },
    accuracyOk() {
      if (!this.coords || this.coords.accuracy == null) return true;
      return this.coords.accuracy <= this.maxAccuracy;
    },
    locationReady() {
      return this.insideFence && this.accuracyOk && !this.locating;
    },
    locationHint() {
      if (this.locating) return "Getting GPS…";
      if (this.gpsError) return this.gpsError;
      if (this.distanceMeters == null) return "Waiting for location";
      const site = (this.fence && this.fence.label) || "office";
      if (this.insideFence) return `Inside fence · ${Math.round(this.distanceMeters)} m from ${site}`;
      return `Outside fence · ${Math.round(this.distanceMeters)} m from nearest site`;
    },
    locationTip() {
      if (this.locating || !this.coords) return null;
      if (!this.insideFence) {
        return "Move closer to a CHED Caraga office site, then tap Refresh location.";
      }
      if (!this.accuracyOk) {
        return "GPS is still imprecise. Step near a window or outdoors, then refresh.";
      }
      return null;
    },
    locationIcon() {
      if (this.locating) return ["bi-arrow-repeat", "spin"];
      if (this.gpsError || (this.distanceMeters != null && !this.insideFence)) return "bi-x-lg";
      if (this.insideFence) return "bi-check-lg";
      return "bi-geo-alt";
    },
    locationCheckClass() {
      if (this.insideFence) return "is-ok";
      if (this.locating || this.distanceMeters == null) return "is-wait";
      return "is-bad";
    },
    accuracyHint() {
      if (!this.coords || this.coords.accuracy == null) return "Will appear with GPS";
      if (!this.accuracyOk) return `Too imprecise (±${Math.round(this.coords.accuracy)} m)`;
      return `±${Math.round(this.coords.accuracy)} m · OK`;
    },
    accuracyIcon() {
      if (!this.coords) return "bi-reception-4";
      return this.accuracyOk ? "bi-check-lg" : "bi-x-lg";
    },
    accuracyCheckClass() {
      if (!this.coords) return "is-wait";
      return this.accuracyOk ? "is-ok" : "is-bad";
    },
    canSubmit() {
      return this.canPunch && this.insideFence && this.accuracyOk && !!this.photoBlob && !this.submitting && !this.punchInFlight;
    },
  },
  watch: {
    coords() {
      if (this.step === 1) this.renderMap();
    },
    fence: {
      deep: true,
      handler() {
        if (this.step === 1) this.renderMap();
      },
    },
    fences: {
      deep: true,
      handler() {
        if (this.step === 1) this.renderMap();
      },
    },
    step(newStep, oldStep) {
      if (oldStep === 1 && newStep !== 1) {
        this.destroyMap();
      }
      if (newStep === 1) {
        this.stopCamera();
        this.$nextTick(() => {
          this.renderMap();
          this.refreshLocation();
        });
      }
      if (newStep === 2) {
        this.$nextTick(() => {
          if (!this.photoBlob) this.startCamera();
        });
      }
      if (newStep === 3) {
        this.stopCamera();
      }
    },
  },
  async mounted() {
    await this.loadStatus();
    if (this.canPunch) {
      this.step = 1;
      this.$nextTick(() => {
        this.renderMap();
        this.refreshLocation();
      });
    }
  },
  beforeDestroy() {
    this.stopCamera();
    this.destroyMap();
    this.revokePhotoObjectUrl();
  },
  methods: {
    async loadStatus() {
      try {
        const { data } = await axios.get("api/attendance/punch-status");
        this.canPunch = !!data.can_punch;
        this.personnelLinked = data.personnel_linked !== false;
        this.punchWindow = data.punch_window || null;
        this.passSlipReturn = data.pass_slip_return || null;
        this.statusMessage = data.message || null;
        this.fence = data.fence || this.fence;
        this.fences = Array.isArray(data.fences) && data.fences.length
          ? data.fences
          : (data.fence ? [data.fence] : this.fences);
        this.maxAccuracy = data.max_accuracy_meters || 200;
        this.nextStatus = data.next_status || "IN";
        this.punchLabel = data.punch_label || "Time In";
        this.logs = data.logs || [];
      } catch (e) {
        this.canPunch = false;
        this.personnelLinked = false;
        this.statusMessage = e.response?.data?.message || "Unable to load punch status.";
      }
    },
    goToLocation() {
      this.step = 1;
    },
    goToPhoto() {
      if (!this.locationReady) return;
      this.step = 2;
    },
    goToConfirm() {
      if (!this.photoBlob) return;
      this.step = 3;
    },
    refreshLocation() {
      if (!navigator.geolocation) {
        this.gpsError = "Geolocation is not supported on this device.";
        return;
      }
      this.locating = true;
      this.gpsError = null;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.coords = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          };
          this.updateNearestFence();
          this.locating = false;
        },
        (err) => {
          this.gpsError = err.message || "Unable to get location. Allow location access and try again.";
          this.locating = false;
        },
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
      );
    },
    updateNearestFence() {
      if (!this.coords) return;
      const list = (this.fences && this.fences.length) ? this.fences : [this.fence];
      let best = null;
      list.forEach((site) => {
        if (!site) return;
        const distance = haversineMeters(
          site.lat,
          site.lng,
          this.coords.lat,
          this.coords.lng
        );
        const inside = distance <= (site.radius_meters || 50);
        const candidate = { fence: site, distance, inside };
        if (
          !best ||
          (inside && !best.inside) ||
          (inside === best.inside && distance < best.distance)
        ) {
          best = candidate;
        }
      });
      if (best) {
        this.fence = best.fence;
        this.distanceMeters = best.distance;
      }
    },
    destroyMap() {
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
      this.mapLayers = {
        fenceCircles: [],
        officeMarkers: [],
        youMarker: null,
        accuracyCircle: null,
      };
    },
    async renderMap() {
      const el = this.$refs.punchMap;
      const sites = (this.fences && this.fences.length) ? this.fences : (this.fence ? [this.fence] : []);
      if (!el || !sites.length || this.step !== 1) return;

      let L;
      try {
        L = await loadLeaflet();
      } catch (e) {
        return;
      }
      if (!L || !this.$refs.punchMap || this.step !== 1) return;

      const primary = this.fence || sites[0];
      const office = [primary.lat, primary.lng];

      if (!this.map) {
        this.map = L.map(el, {
          zoomControl: true,
          attributionControl: true,
        }).setView(office, 17);

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
        }).addTo(this.map);
      }

      (this.mapLayers.fenceCircles || []).forEach((layer) => this.map.removeLayer(layer));
      (this.mapLayers.officeMarkers || []).forEach((layer) => this.map.removeLayer(layer));
      this.mapLayers.fenceCircles = [];
      this.mapLayers.officeMarkers = [];

      sites.forEach((site) => {
        const center = [site.lat, site.lng];
        const radius = site.radius_meters || 50;
        const circle = L.circle(center, {
          radius,
          color: "#0d6efd",
          weight: 2,
          fillColor: "#0d6efd",
          fillOpacity: 0.12,
        }).addTo(this.map);
        const marker = L.circleMarker(center, {
          radius: 7,
          color: "#fff",
          weight: 2,
          fillColor: "#0d6efd",
          fillOpacity: 1,
        })
          .addTo(this.map)
          .bindTooltip(site.label || "Office", { direction: "top" });
        this.mapLayers.fenceCircles.push(circle);
        this.mapLayers.officeMarkers.push(marker);
      });

      if (this.coords) {
        const you = [this.coords.lat, this.coords.lng];
        const youColor = this.insideFence ? "#198754" : "#dc3545";

        if (!this.mapLayers.youMarker) {
          this.mapLayers.youMarker = L.circleMarker(you, {
            radius: 8,
            color: "#fff",
            weight: 2,
            fillColor: youColor,
            fillOpacity: 1,
          })
            .addTo(this.map)
            .bindTooltip("You", { direction: "top" });
        } else {
          this.mapLayers.youMarker.setLatLng(you).setStyle({ fillColor: youColor });
        }

        const acc = Math.max(this.coords.accuracy || 0, 5);
        if (!this.mapLayers.accuracyCircle) {
          this.mapLayers.accuracyCircle = L.circle(you, {
            radius: acc,
            color: youColor,
            weight: 1,
            fillColor: youColor,
            fillOpacity: 0.1,
          }).addTo(this.map);
        } else {
          this.mapLayers.accuracyCircle.setLatLng(you).setRadius(acc).setStyle({
            color: youColor,
            fillColor: youColor,
          });
        }

        const bounds = L.latLngBounds([you]);
        this.mapLayers.fenceCircles.forEach((circle) => bounds.extend(circle.getBounds()));
        bounds.extend(this.mapLayers.accuracyCircle.getBounds());
        this.map.fitBounds(bounds.pad(0.35));
      } else {
        const bounds = L.latLngBounds(sites.map((site) => [site.lat, site.lng]));
        this.mapLayers.fenceCircles.forEach((circle) => bounds.extend(circle.getBounds()));
        this.map.fitBounds(bounds.pad(0.35));
      }

      this.$nextTick(() => {
        if (this.map) this.map.invalidateSize();
      });
    },
    async startCamera() {
      this.cameraBusy = true;
      this.stopCamera();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: false,
        });
        this.mediaStream = stream;
        await this.$nextTick();
        const video = this.$refs.video;
        if (video) {
          video.srcObject = stream;
          await video.play();
        }
        this.cameraReady = true;
      } catch (e) {
        toastr.error("Camera access is required for Time In / Out.", "Camera");
        this.cameraReady = false;
      } finally {
        this.cameraBusy = false;
      }
    },
    stopCamera() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((t) => t.stop());
        this.mediaStream = null;
      }
      this.cameraReady = false;
    },
    capturePhoto() {
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      if (!video || !canvas || !video.videoWidth) {
        toastr.error("Camera is not ready yet.", "Camera");
        return;
      }
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            toastr.error("Failed to capture photo.", "Camera");
            return;
          }
          if (this.photoPreview) URL.revokeObjectURL(this.photoPreview);
          this.photoBlob = blob;
          this.photoPreview = URL.createObjectURL(blob);
          this.stopCamera();
        },
        "image/jpeg",
        0.85
      );
    },
    retakePhoto() {
      if (this.photoPreview) URL.revokeObjectURL(this.photoPreview);
      this.photoPreview = null;
      this.photoBlob = null;
      this.startCamera();
    },
    clearPhoto() {
      if (this.photoPreview) URL.revokeObjectURL(this.photoPreview);
      this.photoPreview = null;
      this.photoBlob = null;
    },
    async submitPunch() {
      if (!this.canSubmit || this.punchInFlight) return;
      this.submitting = true;
      this.punchInFlight = true;
      try {
        const form = new FormData();
        form.append("latitude", String(this.coords.lat));
        form.append("longitude", String(this.coords.lng));
        if (this.coords.accuracy != null) form.append("accuracy", String(this.coords.accuracy));
        form.append("photo", this.photoBlob, "punch.jpg");

        const { data } = await axios.post("api/attendance/punch", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toastr.success(data.message || "Punch recorded.", "Attendance");
        this.clearPhoto();
        await this.loadStatus();
        this.step = 1;
        this.$nextTick(() => {
          this.renderMap();
          this.refreshLocation();
        });
      } catch (e) {
        toastr.error(e.response?.data?.message || "Failed to record punch.", "Attendance");
      } finally {
        this.submitting = false;
        this.punchInFlight = false;
      }
    },
    formatTime(t) {
      if (!t) return "";
      return String(t).slice(0, 8);
    },
  },
};
</script>

<style scoped>
.punch-page {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
}

.punch-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.punch-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.punch-header-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #e8f0fe;
  color: #0d6efd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.punch-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a2340;
}

.punch-sub {
  font-size: 0.8rem;
  color: #8a94a6;
}

.next-badge {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
}

.next-in {
  background: #d1e7dd;
  color: #0f5132;
}

.next-out {
  background: #cfe2ff;
  color: #0d6efd;
}

.punch-alert {
  background: #fff3cd;
  border: 1.5px solid #ffe69c;
  color: #664d03;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 0.9rem;
  margin-bottom: 12px;
}

.punch-info {
  background: #e8f4fd;
  border-color: #b6daff;
  color: #084298;
}

.wizard-steps {
  list-style: none;
  margin: 0 0 14px;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.wizard-step {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1.5px solid #e9ecef;
  background: #fff;
  color: #8a94a6;
  min-width: 0;
}

.wizard-step.is-active {
  border-color: #0d6efd;
  background: #f5f9ff;
  color: #1a2340;
}

.wizard-step.is-done {
  border-color: #c7ebd6;
  background: #f0faf4;
  color: #0f5132;
}

.wizard-num {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  background: #eef1f5;
  color: inherit;
  flex-shrink: 0;
}

.is-active .wizard-num {
  background: #0d6efd;
  color: #fff;
}

.is-done .wizard-num {
  background: #198754;
  color: #fff;
}

.wizard-label {
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.punch-panel {
  background: #fff;
  border: 1.5px solid #e9ecef;
  border-radius: 14px;
  padding: 16px;
}

.wizard-panel {
  margin-bottom: 12px;
}

.panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.panel-head h6 {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 700;
  color: #1a2340;
}

.step-lead {
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: #8a94a6;
  line-height: 1.4;
}

.panel-hint {
  font-size: 0.75rem;
  color: #8a94a6;
  font-weight: 600;
}

.step-tip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff8e6;
  border: 1.5px solid #ffe69c;
  color: #664d03;
  font-size: 0.8rem;
  line-height: 1.4;
}

.step-tip i {
  margin-top: 1px;
  flex-shrink: 0;
}

.camera-wrap {
  position: relative;
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  overflow: hidden;
  background: #f8f9fb;
  border: 1.5px solid #e9ecef;
  max-height: 360px;
}

.camera-wrap.ready,
.camera-wrap.captured {
  border-color: #0d6efd;
}

.camera-view {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.camera-empty {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #8a94a6;
  font-size: 0.85rem;
}

.camera-empty i {
  font-size: 1.6rem;
  color: #adb5bd;
}

.camera-actions,
.wizard-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.wizard-actions {
  margin-top: 16px;
}

.wizard-actions .btn-punch-primary,
.wizard-actions .btn-punch-submit {
  flex: 1;
}

.btn-punch-outline,
.btn-punch-primary,
.btn-punch-submit,
.btn-refresh {
  font-family: inherit;
  cursor: pointer;
}

.btn-punch-outline,
.btn-punch-primary {
  border-radius: 8px;
  padding: 0.55rem 0.95rem;
  font-size: 0.88rem;
  font-weight: 600;
  border: 1.5px solid #dee2e6;
  background: #fff;
  color: #495057;
}

.btn-punch-outline:hover:not(:disabled) {
  border-color: #0d6efd;
  color: #0d6efd;
}

.btn-punch-primary {
  background: #0d6efd;
  border-color: #0d6efd;
  color: #fff;
}

.btn-punch-primary:hover:not(:disabled) {
  background: #0b5ed7;
}

.btn-punch-outline:disabled,
.btn-punch-primary:disabled,
.btn-punch-submit:disabled,
.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-refresh {
  border: 1.5px solid #dee2e6;
  background: #fff;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.btn-refresh:hover:not(:disabled) {
  border-color: #0d6efd;
  color: #0d6efd;
}

.btn-refresh.spinning i,
.spin {
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.check-list {
  display: grid;
  gap: 8px;
  margin-bottom: 12px;
}

.punch-map-wrap {
  position: relative;
  margin-bottom: 8px;
  border-radius: 12px;
  overflow: hidden;
  border: 1.5px solid #e9ecef;
  background: #f8f9fb;
}

.punch-map {
  height: 240px;
  width: 100%;
  z-index: 0;
}

.punch-map-empty {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: rgba(248, 249, 251, 0.88);
  color: #8a94a6;
  font-size: 0.82rem;
  pointer-events: none;
}

.punch-map-empty i {
  font-size: 1.4rem;
  color: #adb5bd;
}

.punch-map-legend {
  position: absolute;
  left: 8px;
  bottom: 8px;
  z-index: 400;
  display: flex;
  gap: 6px;
  pointer-events: none;
}

.punch-map-legend span {
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.18rem 0.45rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #e9ecef;
  color: #495057;
}

.punch-map-legend .leg-office::before,
.punch-map-legend .leg-you::before {
  content: "";
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 5px;
  vertical-align: middle;
}

.punch-map-legend .leg-office::before {
  background: #0d6efd;
}

.punch-map-legend .leg-you.ok::before {
  background: #198754;
}

.punch-map-legend .leg-you.bad::before {
  background: #dc3545;
}

.check-item {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 10px;
  align-items: start;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1.5px solid #eef1f5;
  background: #f8f9fb;
}

.check-item.is-ok {
  background: #f0faf4;
  border-color: #c7ebd6;
}

.check-item.is-bad {
  background: #fff5f5;
  border-color: #f1c0c5;
}

.check-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef1f5;
  color: #6c757d;
  font-size: 0.85rem;
}

.is-ok .check-icon {
  background: #d1e7dd;
  color: #198754;
}

.is-bad .check-icon {
  background: #f8d7da;
  color: #dc3545;
}

.check-label {
  font-size: 0.82rem;
  font-weight: 700;
  color: #1a2340;
}

.check-text {
  font-size: 0.78rem;
  color: #8a94a6;
  margin-top: 2px;
  line-height: 1.35;
}

.confirm-grid {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 12px;
  align-items: stretch;
}

@media (max-width: 560px) {
  .confirm-grid {
    grid-template-columns: 1fr;
  }

  .wizard-steps {
    gap: 6px;
  }

  .wizard-label {
    font-size: 0.75rem;
  }
}

.confirm-main {
  display: grid;
  gap: 10px;
}

.confirm-row {
  display: grid;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8f9fb;
  border: 1.5px solid #eef1f5;
}

.confirm-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #8a94a6;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.confirm-value {
  font-size: 0.88rem;
  font-weight: 600;
  color: #1a2340;
}

.confirm-value.mono {
  font-family: monospace;
  font-weight: 500;
  font-size: 0.8rem;
}

.confirm-photo {
  border-radius: 12px;
  overflow: hidden;
  border: 1.5px solid #e9ecef;
  background: #f8f9fb;
  min-height: 160px;
}

.confirm-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  min-height: 160px;
}

.confirm-photo-empty {
  height: 100%;
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  font-size: 0.85rem;
}

.btn-punch-submit {
  width: 100%;
  border: 0;
  border-radius: 10px;
  min-height: 44px;
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
}

.btn-punch-submit.is-in {
  background: #198754;
}

.btn-punch-submit.is-in:hover:not(:disabled) {
  background: #157347;
}

.btn-punch-submit.is-out {
  background: #0d6efd;
}

.btn-punch-submit.is-out:hover:not(:disabled) {
  background: #0b5ed7;
}

.coord-note {
  margin: 0 0 4px;
  font-size: 0.72rem;
  color: #adb5bd;
  font-family: monospace;
}

.today-panel {
  margin-bottom: 8px;
}

.today-empty {
  color: #8a94a6;
  font-size: 0.85rem;
  padding: 4px 0;
}

.today-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.today-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f5;
}

.log-main {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active { transition: opacity 0.15s; }
.modal-fade-enter,
.modal-fade-leave-to { opacity: 0; }

.today-list li:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.log-time {
  font-weight: 700;
  color: #1a2340;
  min-width: 4.5rem;
  font-variant-numeric: tabular-nums;
}

.status-badge,
.source-badge {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
}

.badge-in {
  background: #d1e7dd;
  color: #0f5132;
}

.badge-out {
  background: #cfe2ff;
  color: #0d6efd;
}

.source-badge {
  margin-left: 0;
}

.src-web {
  background: #e8f0fe;
  color: #0d6efd;
}

.src-bio {
  background: #eef1f5;
  color: #6c757d;
}
</style>
